import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Autocomplete,
  Paper,
  Typography,
  Chip,
  Stack,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  InputAdornment,
  Badge,
  Tooltip,
  FormControl,
  Select,
  InputLabel,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import {
  NavigateBefore,
  NavigateNext,
  Search,
  FilterList,
  Close,
  Person,
  History,
  Numbers,
  Download as DownloadIcon,
  Payment as PaymentIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { SalaryPayment } from '../../utils/salary.types';
import { Utils } from '../../utils/common-utils';

interface SalaryNavigationBarProps {
  currentSalaryId: string;
  onSalaryChange: (salaryId: string) => void;
  salaries?: SalaryPayment[];
  currentSalaryDetails?: any;
  onLoadMore?: () => void;
  hasMoreData?: boolean;
  loadingMore?: boolean;
  onDirectSearch?: (txnId: string) => Promise<boolean>;
  onDownload?: (salaryId: string, employeeName: string) => void;
  onShare?: (salary: any) => void;
  onMarkAsPaid?: (salary: any) => void;
  onDelete?: (salaryId: string) => void;
}

export const SalaryNavigationBar: React.FC<SalaryNavigationBarProps> = ({
  currentSalaryId,
  onSalaryChange,
  salaries = [],
  currentSalaryDetails,
  onLoadMore,
  hasMoreData = false,
  loadingMore = false,
  onDirectSearch,
  onDownload,
  onShare,
  onMarkAsPaid,
  onDelete,
}) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [recentDrawerOpen, setRecentDrawerOpen] = useState(false);
  const [directSearchOpen, setDirectSearchOpen] = useState(false);
  const [directSearchValue, setDirectSearchValue] = useState('');
  const [filteredSalaries, setFilteredSalaries] = useState<SalaryPayment[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter states
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('ALL');
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');

  useEffect(() => {
    // Find current salary index
    const index = salaries.findIndex(sal => sal._id === currentSalaryId);
    setCurrentIndex(index);
  }, [currentSalaryId, salaries]);

  useEffect(() => {
    // Apply filters
    let filtered = [...salaries];

    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(sal => sal.paymentStatus === statusFilter);
    }

    if (paymentTypeFilter !== 'ALL') {
      filtered = filtered.filter(sal => sal.paymentType === paymentTypeFilter);
    }

    if (dateFrom) {
      filtered = filtered.filter(sal => sal.createdAt && new Date(sal.createdAt) >= dateFrom);
    }

    if (dateTo) {
      filtered = filtered.filter(sal => sal.createdAt && new Date(sal.createdAt) <= dateTo);
    }

    if (employeeFilter) {
      filtered = filtered.filter(sal => {
        const employeeName = sal.employee?.name || '';
        return employeeName.toLowerCase().includes(employeeFilter.toLowerCase());
      });
    }

    if (amountMin) {
      filtered = filtered.filter(sal => Number(sal.netSalary) >= parseFloat(amountMin));
    }

    if (amountMax) {
      filtered = filtered.filter(sal => Number(sal.netSalary) <= parseFloat(amountMax));
    }

    setFilteredSalaries(filtered);
  }, [salaries, statusFilter, paymentTypeFilter, dateFrom, dateTo, employeeFilter, amountMin, amountMax]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const filteredSalaryData = filteredSalaries[currentIndex - 1];
      onSalaryChange(filteredSalaryData._id);
    }
  };

  const handleNext = () => {
    // Check if we need to load more data when approaching the end
    const threshold = Math.floor(filteredSalaries.length * 0.8);
    if (currentIndex >= threshold && hasMoreData && onLoadMore && !loadingMore) {
      onLoadMore();
    }

    if (currentIndex < filteredSalaries.length - 1) {
      const filteredSalaryData = filteredSalaries[currentIndex + 1];
      onSalaryChange(filteredSalaryData._id);
    }
  };

  const handleDirectSearch = async () => {
    if (onDirectSearch && directSearchValue.trim()) {
      const searchTerm = directSearchValue.trim();
      const success = await onDirectSearch(searchTerm);

      if (success) {
        setSearchQuery(searchTerm);
        setIsSearchMode(true);
        setDirectSearchOpen(false);
        setDirectSearchValue('');
      } else {
        setDirectSearchOpen(false);
        setDirectSearchValue('');
      }
    }
  };

  const clearSearchMode = () => {
    setIsSearchMode(false);
    setSearchQuery('');
    window.location.reload();
  };

  const handleKeyPress = (event: KeyboardEvent) => {
    if (event.altKey && event.key === 'ArrowLeft') {
      handlePrevious();
    } else if (event.altKey && event.key === 'ArrowRight') {
      handleNext();
    } else if (event.ctrlKey && event.key === 'f') {
      event.preventDefault();
      setSearchOpen(true);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex]);

  const getActiveFiltersCount = () => {
    let count = 0;
    if (statusFilter !== 'ALL') count++;
    if (paymentTypeFilter !== 'ALL') count++;
    if (dateFrom || dateTo) count++;
    if (employeeFilter) count++;
    if (amountMin || amountMax) count++;
    return count;
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          m: 0,
          border: '1px solid #e9ecf1',
          borderRadius: '12px',
          backgroundColor: '#fff'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ position: 'relative' }}>
          {/* Navigation Controls */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Previous Salary (Alt + ←)">
              <IconButton
                onClick={handlePrevious}
                disabled={currentIndex <= 0}
                size="small"
              >
                <NavigateBefore />
              </IconButton>
            </Tooltip>

            <Stack sx={{ px: 1, minWidth: '150px', textAlign: 'center' }}>
              <Typography variant="body2">
                {currentIndex + 1} of {filteredSalaries.length}
                {loadingMore && <CircularProgress size={12} sx={{ ml: 1 }} />}
                {hasMoreData && !loadingMore && !isSearchMode && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' +'}
                  </Typography>
                )}
              </Typography>
              {isSearchMode && (
                <Chip
                  label={`Search: "${searchQuery}"`}
                  size="small"
                  onDelete={clearSearchMode}
                  color="primary"
                  variant="outlined"
                  sx={{ mt: 0.5, fontSize: '0.7rem' }}
                />
              )}
            </Stack>

            <Tooltip title="Next Salary (Alt + →)">
              <IconButton
                onClick={handleNext}
                disabled={currentIndex >= filteredSalaries.length - 1}
                size="small"
              >
                <NavigateNext />
              </IconButton>
            </Tooltip>
          </Stack>

          <Divider orientation="vertical" flexItem />

          {/* Quick Search */}
          <Autocomplete
            sx={{ minWidth: 250 }}
            size="small"
            open={searchOpen}
            onOpen={() => setSearchOpen(true)}
            onClose={() => setSearchOpen(false)}
            options={filteredSalaries}
            getOptionLabel={(option) => {
              const employeeName = option.employee?.name || 'Unknown';
              return `${option.txnId || 'N/A'} - ${employeeName}`;
            }}
            renderOption={(props, option) => {
              const employeeName = option.employee?.name || 'Unknown';
              return (
                <Box component="li" {...props}>
                  <Stack>
                    <Typography variant="body2">TXN #{option.txnId || 'N/A'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {employeeName} • {Utils.formatNumber(parseFloat(option.netSalary as any || 0).toFixed(2), true, true)}
                    </Typography>
                  </Stack>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search loaded salary slips"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            onChange={(_, value) => {
              if (value) {
                onSalaryChange(value._id);
              }
            }}
          />

          {/* Direct TXN ID Search */}
          {onDirectSearch && (
            <>
              <Tooltip title="Search by TXN ID">
                <IconButton
                  onClick={() => setDirectSearchOpen(!directSearchOpen)}
                  size="small"
                  sx={{
                    backgroundColor: directSearchOpen ? 'primary.main' : 'transparent',
                    color: directSearchOpen ? 'white' : 'inherit',
                    '&:hover': {
                      backgroundColor: directSearchOpen ? 'primary.dark' : 'rgba(0,0,0,0.04)'
                    }
                  }}
                >
                  <Numbers />
                </IconButton>
              </Tooltip>

              {directSearchOpen && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    mt: 1,
                    zIndex: 1300,
                    minWidth: 320
                  }}
                >
                  <Paper
                    elevation={8}
                    sx={{
                      p: 2,
                      border: '1px solid #e9ecf1',
                      borderRadius: '12px',
                      backgroundColor: '#fff'
                    }}
                  >
                    <Stack spacing={2}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Numbers fontSize="small" color="primary" />
                        <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 600 }}>
                          Search Salary
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDirectSearchOpen(false);
                            setDirectSearchValue('');
                          }}
                          sx={{ ml: 'auto' }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Stack>

                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter TXN ID or partial match..."
                        value={directSearchValue}
                        onChange={(e) => setDirectSearchValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleDirectSearch();
                          }
                          if (e.key === 'Escape') {
                            setDirectSearchOpen(false);
                            setDirectSearchValue('');
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                          sx: { borderRadius: '8px' }
                        }}
                        autoFocus
                      />

                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          onClick={handleDirectSearch}
                          disabled={!directSearchValue.trim()}
                          size="small"
                          sx={{ textTransform: 'none', borderRadius: '8px', flex: 1 }}
                        >
                          Search Salaries
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setDirectSearchOpen(false);
                            setDirectSearchValue('');
                          }}
                          size="small"
                          sx={{ textTransform: 'none', borderRadius: '8px' }}
                        >
                          Cancel
                        </Button>
                      </Stack>

                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        💡 Tip: Press Enter to search, Escape to close
                      </Typography>
                    </Stack>
                  </Paper>
                </Box>
              )}
            </>
          )}

          {/* Filter Button */}
          <Tooltip title="Filter Salaries">
            <IconButton onClick={() => setFilterDrawerOpen(true)}>
              <Badge badgeContent={getActiveFiltersCount()} color="primary">
                <FilterList />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Recent Salaries */}
          <Tooltip title="Recent Salaries">
            <IconButton onClick={() => setRecentDrawerOpen(true)}>
              <History />
            </IconButton>
          </Tooltip>

          {/* Action Buttons */}
          {currentSalaryDetails && (
            <>
              <Divider orientation="vertical" flexItem />

              {onDownload && (
                <Tooltip title="Download Salary Slip">
                  <IconButton
                    size="small"
                    onClick={() => onDownload(currentSalaryDetails._id, currentSalaryDetails.employee?.name || 'Employee')}
                    sx={{
                      backgroundColor: '#f5f5f5',
                      height: 35,
                      width: 35,
                      mr: 2.5,
                      '&:hover': {
                        backgroundColor: '#e0e0e0'
                      }
                    }}
                  >
                    <DownloadIcon fontSize="small" color="success" />
                  </IconButton>
                </Tooltip>
              )}

              {onShare && (
                <Tooltip title="Share Salary Slip">
                  <IconButton
                    size="small"
                    onClick={() => onShare(currentSalaryDetails)}
                    sx={{
                      backgroundColor: '#f5f5f5',
                      height: 35,
                      width: 35,
                      mr: 2.5,
                      '&:hover': {
                        backgroundColor: '#e0e0e0'
                      }
                    }}
                  >
                    <ShareIcon fontSize="small" color="info" />
                  </IconButton>
                </Tooltip>
              )}

              {onMarkAsPaid && (currentSalaryDetails.paymentStatus === 'PENDING' || currentSalaryDetails.paymentStatus === 'PROCESSED') && (
                <Tooltip title="Mark as Paid">
                  <IconButton
                    size="small"
                    onClick={() => onMarkAsPaid(currentSalaryDetails)}
                    sx={{
                      backgroundColor: '#f5f5f5',
                      height: 35,
                      width: 35,
                      mr: 2.5,
                      '&:hover': {
                        backgroundColor: '#e0e0e0'
                      }
                    }}
                  >
                    <PaymentIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              )}

              {onDelete && (
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={() => onDelete(currentSalaryDetails._id)}
                    sx={{
                      backgroundColor: '#f5f5f5',
                      height: 35,
                      width: 35,
                      mr: 2.5,
                      '&:hover': {
                        backgroundColor: '#e0e0e0'
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" color="error" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Stack>
      </Paper>

      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
      >
        <Box sx={{ width: 350, p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Filter Salaries</Typography>
            <IconButton onClick={() => setFilterDrawerOpen(false)}>
              <Close />
            </IconButton>
          </Stack>

          <Stack spacing={3}>
            {/* Status Filter */}
            <FormControl fullWidth size="small">
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={statusFilter}
                label="Payment Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="PROCESSED">Processed</MenuItem>
                <MenuItem value="PAID">Paid</MenuItem>
              </Select>
            </FormControl>

            {/* Payment Type Filter */}
            <FormControl fullWidth size="small">
              <InputLabel>Payment Type</InputLabel>
              <Select
                value={paymentTypeFilter}
                label="Payment Type"
                onChange={(e) => setPaymentTypeFilter(e.target.value)}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="MONTHLY_SALARY">Monthly Salary</MenuItem>
                <MenuItem value="DAILY_WAGE">Daily Wage</MenuItem>
              </Select>
            </FormControl>

            {/* Date Range */}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="From Date"
                value={dateFrom}
                onChange={(newValue: any) => setDateFrom(newValue)}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
              <DatePicker
                label="To Date"
                value={dateTo}
                onChange={(newValue: any) => setDateTo(newValue)}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </LocalizationProvider>

            {/* Employee Filter */}
            <TextField
              size="small"
              fullWidth
              label="Employee Name"
              value={employeeFilter}
              onChange={(e) => setEmployeeFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Amount Range */}
            <Stack direction="row" spacing={2}>
              <TextField
                size="small"
                label="Min Amount"
                type="number"
                value={amountMin}
                onChange={(e) => setAmountMin(e.target.value)}
              />
              <TextField
                size="small"
                label="Max Amount"
                type="number"
                value={amountMax}
                onChange={(e) => setAmountMax(e.target.value)}
              />
            </Stack>

            {/* Action Buttons */}
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  setStatusFilter('ALL');
                  setPaymentTypeFilter('ALL');
                  setDateFrom(null);
                  setDateTo(null);
                  setEmployeeFilter('');
                  setAmountMin('');
                  setAmountMax('');
                }}
              >
                Clear Filters
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={() => setFilterDrawerOpen(false)}
              >
                Apply
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Drawer>

      {/* Recent Salaries Drawer */}
      <Drawer
        anchor="right"
        open={recentDrawerOpen}
        onClose={() => setRecentDrawerOpen(false)}
      >
        <Box sx={{ width: 350, p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Recent Salaries</Typography>
            <IconButton onClick={() => setRecentDrawerOpen(false)}>
              <Close />
            </IconButton>
          </Stack>

          <List>
            {filteredSalaries.slice(0, 10).map((salary, index) => (
              <React.Fragment key={salary._id}>
                <ListItemButton
                  selected={salary._id === currentSalaryId}
                  onClick={() => {
                    onSalaryChange(salary._id);
                    setRecentDrawerOpen(false);
                  }}
                >
                  <ListItemText
                    primary={`TXN #${salary.txnId || 'N/A'}`}
                    secondary={
                      <Stack spacing={0.5}>
                        <Typography variant="caption">
                          {salary.employee?.name || 'Unknown'}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Chip
                            label={Utils.formatNumber(parseFloat(salary.netSalary as any || 0).toFixed(2), true, true)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            label={salary.paymentStatus}
                            size="small"
                            color={salary.paymentStatus === 'PAID' ? 'success' : 'default'}
                          />
                        </Stack>
                      </Stack>
                    }
                  />
                </ListItemButton>
                {index < 9 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default SalaryNavigationBar;
